import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  LoadingSpinner,
  SubscriptionCardOverlayTag,
  SubscriptionCardProps,
} from '../../components/Molecules';
import { SubscriptionTemplate } from '../../components/Templates';
import { WebPlanTier } from '../../constants/allowance';
import { AppToastErrorOption } from '../../constants/toast';
import useCancelRedemption from '../../hooks/useCancelRedemption';
import useLoadRedemption from '../../hooks/useLoadRedemption';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { SubscriptionType } from '../../models/SpaceStore';
import { useRewardStore } from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

const { createRedemption } = useRewardStore.getState();

const tiers: (WebPlanTier | undefined)[] = [
  undefined,
  WebPlanTier.Tier1,
  WebPlanTier.Tier2,
  WebPlanTier.Tier3,
];

const SubscriptionScreen = () => {
  // const navigate = useNavigate();
  const { t } = useTranslation();

  useRedirectEntry({ allowTrial: false });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const account = useUserStore((state) => state.getAccount(activeUserId ?? ''));
  const profile = useUserStore((state) => state.getProfile(activeUserId ?? ''));
  const rewardOfferStore = 'system';
  const rewardOfferId = 'web-plan';

  const [isLoading, setIsLoading] = useState(false);
  const [rewardRedemptionId, setRewardRedemptionId] = useState<
    string | undefined
  >();

  const cancelRedemption = useCancelRedemption(rewardOfferStore, rewardOfferId);

  const onClickPurchase = useCallback(() => {
    setIsLoading(true);

    createRedemption(rewardOfferStore, {
      rewardOfferId,
      quantity: 1,
      paymentGateway: 'stripe',
      variant: { tier: WebPlanTier.Tier1, period: SubscriptionType.Monthly }, // TODO
      currency: profile?.localCurrency,
    })
      .then((res) => {
        if (res) {
          setRewardRedemptionId(res.rewardRedemptionId);
        } else {
          setIsLoading(false);
          toast(
            t('Failed to create order. Please refresh the page and try again.'),
            AppToastErrorOption,
          );
          cancelRedemption();
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast(
          t('Failed to create order. Please refresh the page and try again.'),
          AppToastErrorOption,
        );
        cancelRedemption();
      });
  }, [cancelRedemption, profile?.localCurrency, t]);

  useLoadRedemption(rewardOfferStore, rewardRedemptionId, setIsLoading);

  const currentPlanProps: SubscriptionCardProps = useMemo(() => {
    return {
      tier: account?.activeWebPlanTier,
      period: undefined,
      overlayTag: SubscriptionCardOverlayTag.Current,
    };
  }, [account?.activeWebPlanTier]);

  const upgradePlanProps: SubscriptionCardProps[] = useMemo(() => {
    const currentTier = account?.activeWebPlanTier;
    const currentPeriod = account?.webPlanSubscription?.period;

    const planProps: SubscriptionCardProps[] = [];
    const index = tiers.findIndex((tier) => tier === currentTier) ?? 0;

    if (index < tiers.length - 1) {
      planProps.push({
        tier: tiers[index + 1],
        period: currentPeriod,
        currency: profile?.localCurrency,
        onClickUpgrade: index === 0 ? onClickPurchase : undefined,
      });
    }

    return planProps;
  }, [
    account?.activeWebPlanTier,
    account?.webPlanSubscription,
    profile?.localCurrency,
    onClickPurchase,
  ]);

  return (
    <>
      {isLoading && <LoadingSpinner loading={isLoading} />}
      <SubscriptionTemplate
        currentPlanProps={currentPlanProps}
        upgradePlansProps={upgradePlanProps}
        downgradePlansProps={[]}
      />
    </>
  );
};

export default SubscriptionScreen;
